import React, { FC, useCallback, useEffect, useState } from "react";
import {
  checkFileExists,
  checkFolderExists,
  saveFile,
  saveFolder,
} from "../../services/folderService";
import UploadUI from "./UploadUI";
import {
  CreateResult,
  FileDetailsDto,
  FileExistsDto,
  FolderExistsDto,
  PortalFolderDto,
} from "../../types/Folder";
import useApi from "../../hooks/useApi";
import { getAllRolesByUser } from "../../services/roleService";
import { RoleDto, RoleFolderPermissionDto } from "../../types/Role";
import { ResponseDto } from "../../types/Helper";
import { FileActions, FolderFileType, NotificationType } from "../../data/Enums";
import { useDispatch } from "react-redux";
import { showAlert } from "../../redux/alertSlice";
import { SEVERITIES, SYSTEMSETTINGS, GLOBALSETTINGS } from "../../data/constants";
import { useUser } from "../../context/UserContext";
import { setFolder } from "../../redux/folderSlice";
import {
  GetAllFilesAndFoldersByUserDto,
  GetFoldersByUserIdDto,
} from "../../types/FolderPermission";
import { setResourceCreated } from "../../redux/resourceCreatedSlice";
import { triggerClearSearch } from "../../redux/clearSearchTextSlice";
import { setRowSelectedFolder } from "../../redux/rowSelectedSlice";
import { setResourceAdded } from "../../redux/resourceAddedSlice";
import { logAuditTrace } from "../../services/auditNavigationTraceService";
import { useSettingValue } from "../../services/useSettingValue";
import {
  DESCRIPTION_LENGTH,
  FILE_CREATED_SUCCESS,
  FILE_PUBLISHED_SUCCESS,
  FOLDER_CREATED_SUCCESS,
  FOLDER_PUBLISHED_SUCCESS,
  GLOBALMESSAGES,
  UPLOADMESSAGES,
  UPLOADVALIDATIONS,
  ZERO_FILE_SIZE,
} from "../../constants/messages";
import {
  getDestinationUrl,
  getUncheckedUsers,
  mapDefaultPermissions,
  mapRolesToPermissions,
  shouldShowMessage,
} from "../../utils/commonHelper";
import { getCurrentESTTime } from "../../utils/dateHelper";

interface UploadComponentProps {
  parentFolderId: number | null;
  onClose: () => void;
  fromGrid: boolean;
}

const UploadComponent: FC<UploadComponentProps> = ({ parentFolderId, onClose, fromGrid }) => {
  const [defaultPermission, setDefaultPermission] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<NotificationType[]>([]);
  const [isValidNotificationType, setIsValidNotificationType] = useState(true);
  const { loading, executeApi } = useApi();
  const dispatch = useDispatch();
  const { userId, userName } = useUser();

  const initialState = {
    description: "",
    rolePermissions: [] as RoleFolderPermissionDto[],
    file: null as File | null,
    validationError: null as string | null,
    dialogOpen: false,
    isInitialLoad: true,
  };
  const [state, setState] = useState(initialState);

  const updateState = useCallback((newState: Partial<typeof state>) => {
    setState((prevState) => ({
      ...prevState,
      ...newState,
    }));
  }, []);
  const attachmentSize = useSettingValue(SYSTEMSETTINGS.ATTACHMENT_SIZE);
  const validatePermissions = useCallback(() => {
    const atLeastOnePermissionChecked = state.rolePermissions.some(
      (permission) =>
        permission.canView ||
        permission.canDownload ||
        permission.canShareToUser ||
        permission.canShareToRole ||
        permission.canEdit ||
        permission.canDelete ||
        permission.canManagePermissions,
    );

    if (atLeastOnePermissionChecked) {
      updateState({ validationError: null });
      return true;
    } else {
      updateState({ validationError: UPLOADVALIDATIONS.PERMISSION_VALIDATION });
      return false;
    }
  }, [state.rolePermissions, updateState]);

  useEffect(() => {
    if (!state.isInitialLoad && state.rolePermissions.length > 0) {
      validatePermissions();
    }
  }, [state.rolePermissions, state.isInitialLoad, validatePermissions]);

  useEffect(() => {
    if (state.isInitialLoad) {
      updateState({ isInitialLoad: false });
    }
  }, [state.isInitialLoad, updateState]);

  useEffect(() => {
    const fetchRoles = async () => {
      if (userId !== null) {
        const roles = await executeApi<number, RoleDto[]>(getAllRolesByUser, userId);
        if (roles !== null) {
          const permissions = mapRolesToPermissions(roles, userId);
          updateState({ rolePermissions: permissions });
        }
      }
    };
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement> | null) => {
    if (!event) {
      // Clear the file when null is passed
      updateState({ description: "", validationError: null });
      return;
    }
    const newDescription = event.target.value;
    updateState({ description: newDescription });

    if (newDescription !== "" || state.file) {
      updateState({ validationError: null });
    }
  };

  const handleFolderExists = async (): Promise<boolean> => {
    const folderData: FolderExistsDto = {
      description: state.description?.trim(),
      folderId: null,
      parentFolderId: parentFolderId,
      createdBy: userId,
    };

    const result = await executeApi<FolderExistsDto, boolean>(checkFolderExists, folderData);

    if (result) {
      updateState({ validationError: UPLOADVALIDATIONS.FOLDER_ALREADY_EXISTS });
    } else {
      updateState({ validationError: null });
    }
    return result;
  };

  const handleFileExists = async (): Promise<boolean> => {
    const fileData: FileExistsDto = {
      fileName: state.file.name?.trim(),
      fileId: null,
      parentFolderId: parentFolderId,
      createdBy: userId,
    };

    const result = await executeApi<FileExistsDto, boolean>(checkFileExists, fileData);

    if (result) {
      updateState({ validationError: UPLOADVALIDATIONS.FILE_ALREADY_EXISTS });
    } else {
      updateState({ validationError: null });
    }
    return result;
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement> | null) => {
    if (!event) {
      // Clear the file when null is passed
      updateState({ file: null, validationError: null });
      return;
    }
    const selectedFile = event.target.files ? event.target.files[0] : null;
    if (selectedFile) {
      updateState({ file: selectedFile });

      if (state.description !== "" || selectedFile) {
        updateState({ validationError: null });
      }
    }
  };

  const handleNotificationChange = (selected: NotificationType[], notificationEnabled: boolean) => {
    setSelectedTypes(selected);
    if (notificationEnabled && selected.length === 0) {
      setIsValidNotificationType(false);
    } else {
      setIsValidNotificationType(true);
    }
  };

  const handlePermissionChange = (
    roleId: number,
    field: keyof RoleFolderPermissionDto,
    value: boolean,
  ) => {
    updateState({
      rolePermissions: state.rolePermissions.map((role) =>
        role.roleId === roleId ? { ...role, [field]: value } : role,
      ),
    });
  };

  const handleUserSelectionChange = (roleId: number, selectedUserIds: number[]) => {
    updateState({
      rolePermissions: state.rolePermissions.map((role) =>
        role.roleId === roleId
          ? {
              ...role,
              users: role.users.map((user) => ({
                ...user,
                isChecked: selectedUserIds.includes(user.userId),
              })),
            }
          : role,
      ),
    });
  };

  const handleCreate = async (isPublish: boolean): Promise<CreateResult | null> => {
    let result = null;
    if (state.file !== null) {
      result = await createFile(isPublish);
    } else {
      result = await createFolder(isPublish);
    }
    return result;
  };

  const getFilteredPermissions = () => {
    const hasAnyPermission = (item: RoleFolderPermissionDto): boolean => {
      return (
        item.canView ||
        item.canDownload ||
        item.canShareToUser ||
        item.canShareToRole ||
        item.canEdit ||
        item.canDelete ||
        item.canManagePermissions
      );
    };

    return state.rolePermissions.filter(hasAnyPermission);
  };

  const createFile = async (isPublish: boolean) => {
    if (state.file !== null) {
      const appUrl = getDestinationUrl(parentFolderId);
      const formData = new FormData();
      formData.append("portalFileDto.description", state.file?.name?.trim());
      formData.append("portalFileDto.isPublish", isPublish.toString());
      formData.append(
        "portalFileDto.folderId",
        parentFolderId === null ? "" : parentFolderId.toString(),
      );
      formData.append("portalFileDto.file", state.file);
      formData.append("portalFileDto.createdBy", userId === null ? "" : userId.toString());
      if (isPublish) {
        selectedTypes.forEach((type) =>
          formData.append("portalFileDto.NotificationTypes", type.toString()),
        );
      }

      formData.append("rolePermissions", JSON.stringify(getFilteredPermissions()));
      formData.append("restrictedUsers", JSON.stringify(getUncheckedUsers(state.rolePermissions)));
      formData.append("portalFileDto.applicationUrl", appUrl);
      formData.append("portalFileDto.userName", userName);
      const result = await executeApi<FormData, ResponseDto<FileDetailsDto>>(saveFile, formData);
      if (result?.isSuccess && result.data.fileId !== null) {
        return {
          fileId: result.data.fileId,
          fileUrl: result.data.fileUrl,
          type: FolderFileType[FolderFileType.File],
        };
      } else {
        showToaster(
          result?.errorMessage ? result?.errorMessage : GLOBALMESSAGES.GLOBAL_ERROR,
          SEVERITIES.ERROR,
        );
      }
    }
    return null;
  };

  const createFolder = async (isPublish: boolean) => {
    const folderData: PortalFolderDto = {
      description: state.description?.trim(),
      parentFolderId: parentFolderId,
      isPublish: isPublish,
      createdBy: userId,
      roleFolderPermissions: getFilteredPermissions(),
      restrictedUsers: getUncheckedUsers(state.rolePermissions),
      notificationTypes: isPublish ? selectedTypes : [],
    };
    const result = await executeApi<PortalFolderDto, ResponseDto<number>>(saveFolder, folderData);
    if (result?.isSuccess && result?.data && result?.data > 0) {
      return {
        folderId: result.data,
        type: FolderFileType[FolderFileType.Folder],
      };
    } else {
      showToaster(result?.errorMessage || GLOBALMESSAGES.GLOBAL_ERROR, SEVERITIES.ERROR);
      return null;
    }
  };

  const triggerNewFolder = (folderId: number) => {
    const canViewMax = Math.max(...state.rolePermissions.map((rp) => (rp.canView ? 1 : 0))) > 0;
    const canEditMax = Math.max(...state.rolePermissions.map((rp) => (rp.canEdit ? 1 : 0))) > 0;
    if (canViewMax) {
      const newFolder: GetFoldersByUserIdDto = {
        folderId: folderId,
        folderName: state.description,
        parentFolderId: parentFolderId,
        canView: canViewMax,
        canEdit: canEditMax,
        canDelete: false,
        canDownload: false,
        canManagePermissions: false,
        canShareToRole: false,
        canShareToUser: false,
        childFolderCount: 0,
      };
      dispatch(setFolder({ folderDetails: newFolder, fromGrid: fromGrid }));
    }
  };

  const validateForm = async () => {
    const maxSize = attachmentSize * 1024 * 1024;
    if (state.description.trim() && state.file) {
      updateState({ validationError: UPLOADVALIDATIONS.FOLDERFILEBOTH_VALIDATION });
      return false;
    }

    if (GLOBALSETTINGS.INVALID_FILENAME_CHARS_REGEXP.test(state.description?.trim())) {
      updateState({
        validationError: UPLOADVALIDATIONS.FOLDER_SPEC_CHAR_VALIDATION.replace(
          "{Invalid Chars}",
          GLOBALSETTINGS.INVALID_FILENAME_CHARS_TEXT,
        ),
      });
      return false;
    }

    if (!state.description && !state.file) {
      updateState({ validationError: UPLOADVALIDATIONS.FOLDERFILEEMPTY_VALIDATION });
      return false;
    }

    if (!state.description?.trim() && !state.file) {
      updateState({ validationError: UPLOADVALIDATIONS.FOLDER_WHITESPACE_VALIDATION });
      return false;
    }

    if (state.file && state.file.size === 0) {
      updateState({
        validationError: ZERO_FILE_SIZE(state.file?.name),
      });
      return;
    }

    if (state.file && state.file.size > maxSize) {
      updateState({
        validationError: UPLOADVALIDATIONS.FILE_MAX_SIZE.replace(
          "{attachmentSize}",
          attachmentSize,
        ),
      });
      return false;
    }

    if (state.file && state.file.name?.length > GLOBALSETTINGS.DESCRIPTION_LENGTH) {
      updateState({
        validationError: DESCRIPTION_LENGTH(
          FolderFileType[FolderFileType.File],
          GLOBALSETTINGS.DESCRIPTION_LENGTH,
        ),
      });
      return false;
    }

    if (state.description && state.description?.length > GLOBALSETTINGS.DESCRIPTION_LENGTH) {
      updateState({
        validationError: DESCRIPTION_LENGTH(
          FolderFileType[FolderFileType.Folder],
          GLOBALSETTINGS.DESCRIPTION_LENGTH,
        ),
      });
      return false;
    }

    let alreadyExists = false;
    if (state.description) {
      alreadyExists = await handleFolderExists();
    }

    if (state.file) {
      alreadyExists = await handleFileExists();
    }
    if (alreadyExists) {
      return false;
    }

    const isValid = validatePermissions() && isValidNotificationType;
    return isValid;
  };

  const handleConfirmDialogClose = async (confirmed: boolean) => {
    updateState({ dialogOpen: false });
    const result = await handleCreate(confirmed);
    if (!result) {
      const actionDescription = FileActions.AddNew.toString() + GLOBALMESSAGES.FAILED_ADDITION;
      logAuditTrace(
        {
          userId: userId,
          objectType:
            state.file === null
              ? FolderFileType[FolderFileType.Folder]
              : FolderFileType[FolderFileType.File],
          objectId: null,
          objectName: state.file === null ? state.description : state.file?.name,
          action: actionDescription,
          navigationDateTime: getCurrentESTTime(),
        },
        dispatch,
      );
      return;
    }
    if (state.description !== "") {
      triggerNewFolder(result.folderId as number);
      if (fromGrid) {
        dispatch(setResourceCreated({ parentFolderId }));
        dispatch(triggerClearSearch());
      } else {
        const addedRow: GetAllFilesAndFoldersByUserDto = {
          roleFolderPermissionsId: 0,
          folderId: result.folderId,
          parentFolderId: parentFolderId,
          fileId: -1,
          description: state?.description ?? "",
          isPublish: confirmed,
          createdUserId: userId,
          canView: true,
          canEdit: true,
          canDelete: true,
          canDownload: true,
          canManagePermissions: true,
          canShareToRole: true,
          canShareToUser: true,
          createdBy: userName,
          createdDate: getCurrentESTTime(),
          type: FolderFileType[FolderFileType.Folder],
          totalRecordCount: 0,
        };

        dispatch(
          setResourceAdded({ addedRow: addedRow, type: FolderFileType[FolderFileType.Folder] }),
        );
      }
    } else {
      //for file
      if (fromGrid) {
        dispatch(setRowSelectedFolder({ parentFolderId: parentFolderId }));
      } else {
        const addedRow: GetAllFilesAndFoldersByUserDto = {
          roleFolderPermissionsId: 0,
          folderId: -1,
          parentFolderId: parentFolderId,
          fileId: result.fileId,
          fileUrl: result.fileUrl,
          description: state?.file.name ?? "",
          isPublish: confirmed,
          createdUserId: userId,
          canView: true,
          canEdit: true,
          canDelete: true,
          canDownload: true,
          canManagePermissions: true,
          canShareToRole: true,
          canShareToUser: true,
          createdBy: userName,
          createdDate: getCurrentESTTime(),
          type: FolderFileType[FolderFileType.File],
          totalRecordCount: 0,
        };

        dispatch(
          setResourceAdded({ addedRow: addedRow, type: FolderFileType[FolderFileType.File] }),
        );
      }
    }

    showMessage(confirmed);
    let actionDescription = FileActions.AddNew.toString();
    logAuditTrace(
      {
        userId: userId,
        objectType:
          state.file === null
            ? FolderFileType[FolderFileType.Folder]
            : FolderFileType[FolderFileType.File],
        objectId:
          result.type === FolderFileType[FolderFileType.File] ? result.fileId : result.folderId,
        objectName: state.file === null ? state.description : state.file?.name,
        action: actionDescription,
        navigationDateTime: getCurrentESTTime(),
      },
      dispatch,
    );
  };

  const handleDiscard = () => {
    updateState({ dialogOpen: false });
  };

  const showMessage = (confirmed: boolean) => {
    let message =
      state.file !== null
        ? FILE_CREATED_SUCCESS(state.file.name)
        : FOLDER_CREATED_SUCCESS(state.description);
    if (confirmed) {
      message =
        state.file !== null
          ? FILE_PUBLISHED_SUCCESS(state.file.name)
          : FOLDER_PUBLISHED_SUCCESS(state.description);
    }
    const createMessage = defaultPermission
      ? `${UPLOADMESSAGES.DEFAULT_PERMISSION_TITLE}\n${message}`
      : message;
    showToaster(createMessage, SEVERITIES.SUCCESS);
    handleCreateDialogClose();
  };

  const showToaster = (message: string, severity: (typeof SEVERITIES)[keyof typeof SEVERITIES]) => {
    dispatch(
      showAlert({
        message: message,
        severity: severity,
      }),
    );
  };

  const handleCreateDialogClose = () => {
    setState(initialState);
    onClose();
  };

  const openConfirmDialog = async () => {
    if (await validateForm()) {
      const setDefault = shouldShowMessage(state.rolePermissions, userId);
      if (setDefault) {
        const defaultPermissions = mapDefaultPermissions(state.rolePermissions, userId);
        if (defaultPermissions) {
          updateState({ rolePermissions: defaultPermissions });
          setDefaultPermission(true);
        }
      }
      updateState({ dialogOpen: true });
    }
  };

  return (
    <UploadUI
      description={state.description}
      loading={loading}
      validationError={state.validationError}
      dialogOpen={state.dialogOpen}
      handleDiscard={handleDiscard}
      handleDialogClose={handleConfirmDialogClose}
      handleDescriptionChange={handleDescriptionChange}
      permissions={state.rolePermissions}
      handlePermissionChange={handlePermissionChange}
      handleUserSelectionChange={handleUserSelectionChange}
      file={state.file}
      onFileChange={handleFileChange}
      onSubmit={openConfirmDialog}
      onModalClose={onClose}
      onNotificationChange={handleNotificationChange}
    />
  );
};

export default UploadComponent;
